import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { hojeBR } from '../../utils/data';
import s from '../../pages/PedidosOrcamentos.module.css';

const fmtMoeda = v => Number(v||0).toLocaleString('pt-BR',{style:'currency',currency:'BRL'});
const hoje     = () => hojeBR();

const FORMAS_PAGAMENTO = ['Dinheiro','PIX','Transferência','Boleto','Cartão Débito','Cartão Crédito','Cheque','Outro'];
const FORMA_CREDITO = 'Crédito fornecedor';

// Sem cálculo automático de multa/juros por dias de atraso (isso é só em
// Contas a Receber). Aqui quem manda é o valor digitado: pagou a mais do
// que o documento → a diferença é juros/multa (lançado direto, sem
// checkbox); pagou a menos com "Pago com desconto" marcado → a diferença é
// desconto obtido; pagou a menos sem marcar → baixa parcial, gera título
// residual em aberto pelo restante.
export default function ModalBaixa({ conta, contas, onBaixar, onClose }) {
  const [f, setF] = useState({
    data_pagamento:  hoje(),
    valor_pago:      conta.valor,
    conta_id:        conta.conta_id || '',
    forma_pagamento: 'Dinheiro',
  });
  const [pagoComDesconto, setPagoComDesconto] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [creditoForn, setCreditoForn] = useState(null); // null = sem fornecedor/sem saldo
  const set = (k,v) => setF(p=>({...p,[k]:v}));

  // Saldo de crédito com o fornecedor (gerado por devolução de compra)
  useEffect(() => {
    if (!conta.fornecedor_id) return;
    api.get('/financeiro/creditos-fornecedor', { params: { fornecedor_id: conta.fornecedor_id } })
      .then(({ data }) => { if (data.total > 0) setCreditoForn(data.total); })
      .catch(() => {});
  }, [conta.fornecedor_id]);

  const ehCredito = f.forma_pagamento === FORMA_CREDITO;
  const vTotal   = parseFloat(conta.valor || 0);
  const vPago    = parseFloat(f.valor_pago || 0);
  const vDiff    = Math.round((vPago - vTotal) * 100) / 100;
  const temJuros    = !ehCredito && vDiff > 0.004;
  const temDesconto = !ehCredito && vDiff < -0.004 && pagoComDesconto;
  const parcial      = !ehCredito && vDiff < -0.004 && !pagoComDesconto;
  const vRest        = parcial ? Math.round((vTotal - vPago) * 100) / 100 : 0;

  const confirmar = async () => {
    if (!ehCredito && !f.conta_id) return alert('Selecione a conta financeira.');
    if (vPago <= 0) return alert('Informe um valor válido.');
    if (ehCredito && Math.abs(vDiff) > 0.004)
      return alert('Crédito de fornecedor não combina com juros/multa/desconto — o valor pago tem que bater com o documento (ou ficar menor, gerando restante).');
    if (ehCredito && creditoForn != null && vPago > creditoForn + 0.004)
      return alert(`Crédito insuficiente: disponível ${fmtMoeda(creditoForn)}.`);
    setSalvando(true);
    try {
      await onBaixar({
        ...f, conta_id: ehCredito ? null : f.conta_id, pago_com_desconto: temDesconto,
      });
    } finally { setSalvando(false); }
  };

  return (
    <div className={s.confirmOverlay}>
      <div className={s.confirmBox} style={{width:440}}>
        <div className={s.confirmTitulo}>Baixar Conta a Pagar</div>
        <div className={s.confirmCorpo}>
          <p style={{marginBottom:12}}><strong>{conta.descricao}</strong></p>
          <div style={{display:'flex',flexDirection:'column',gap:8}}>
            <label style={{display:'flex',flexDirection:'column',gap:3,fontSize:12}}>
              Forma de Pagamento *
              <select className={s.selectErp} value={f.forma_pagamento}
                onChange={e=>set('forma_pagamento',e.target.value)}>
                {FORMAS_PAGAMENTO.map(fp=><option key={fp} value={fp}>{fp}</option>)}
                {creditoForn != null && (
                  <option value={FORMA_CREDITO}>
                    {FORMA_CREDITO} (saldo: {fmtMoeda(creditoForn)})
                  </option>
                )}
              </select>
            </label>
            {ehCredito && (
              <div style={{background:'#eff6ff',border:'1px solid #3b82f6',borderRadius:4,
                padding:'8px 12px',fontSize:12,color:'#1e40af'}}>
                💳 Baixa com crédito de devolução de compra — não movimenta conta financeira.
                Saldo disponível: <strong>{fmtMoeda(creditoForn)}</strong>
              </div>
            )}
            <label style={{display:'flex',flexDirection:'column',gap:3,fontSize:12}}>
              Data do Pagamento
              <input type="date" className={s.inputErp} value={f.data_pagamento}
                onChange={e=>set('data_pagamento',e.target.value)} />
            </label>
            <div style={{display:'flex',gap:8}}>
              <label style={{display:'flex',flexDirection:'column',gap:3,fontSize:12,flex:1}}>
                Valor Total
                <input className={s.inputErp} readOnly value={fmtMoeda(vTotal)}
                  style={{background:'#f3f4f6',fontWeight:600,textAlign:'right'}} />
              </label>
              <label style={{display:'flex',flexDirection:'column',gap:3,fontSize:12,flex:1}}>
                Valor Pago *
                <input type="number" step="0.01" min="0.01" className={s.inputErp}
                  value={f.valor_pago}
                  onChange={e=>set('valor_pago',e.target.value)}
                  style={{fontWeight:600,textAlign:'right',
                    borderColor: parcial ? '#f59e0b' : temJuros ? '#92400e' : ''}} />
              </label>
            </div>
            {!ehCredito && temJuros && (
              <div style={{background:'#fffbeb',border:'1px solid #f59e0b',borderRadius:4,
                padding:'8px 12px',fontSize:12,color:'#92400e'}}>
                ⚠️ Valor pago maior que o documento — a diferença de <strong>{fmtMoeda(vDiff)}</strong>{' '}
                será lançada como juros/multa (despesa financeira no DRE). Título quitado por inteiro.
              </div>
            )}
            {!ehCredito && vDiff < -0.004 && (
              <div style={{border:'1px solid #e5e7eb',borderRadius:4,padding:'8px 12px'}}>
                <label style={{display:'flex',alignItems:'center',gap:6,fontSize:12,cursor:'pointer'}}>
                  <input type="checkbox" checked={pagoComDesconto}
                    onChange={e=>setPagoComDesconto(e.target.checked)} />
                  Pago com desconto (quita o título inteiro)
                </label>
                {temDesconto && (
                  <div style={{fontSize:12,color:'#166534',marginTop:6}}>
                    Desconto de <strong>{fmtMoeda(-vDiff)}</strong> lançado como receita financeira
                    no DRE. Título quitado por inteiro.
                  </div>
                )}
              </div>
            )}
            {parcial && (
              <div style={{background:'#fffbeb',border:'1px solid #f59e0b',borderRadius:4,
                padding:'8px 12px',fontSize:12,color:'#92400e'}}>
                ⚠️ Pagamento parcial — será gerada automaticamente uma nova conta em aberto de{' '}
                <strong>{fmtMoeda(vRest)}</strong> para o restante.
              </div>
            )}
            {!ehCredito && (
              <label style={{display:'flex',flexDirection:'column',gap:3,fontSize:12}}>
                Conta Financeira *
                <select className={s.selectErp} value={f.conta_id} onChange={e=>set('conta_id',e.target.value)}>
                  <option value="">— Selecione —</option>
                  {contas.map(c=><option key={c.id} value={c.id}>{c.nome}</option>)}
                </select>
              </label>
            )}
          </div>
        </div>
        <div className={s.confirmBtns}>
          <button className={`${s.btnErp} ${s.btnErpPrimario}`} onClick={confirmar} disabled={salvando}>
            {salvando ? 'Baixando...'
              : temJuros ? `Confirmar Pagamento de ${fmtMoeda(vPago)} (com juros/multa)`
              : temDesconto ? `Confirmar Pagamento de ${fmtMoeda(vPago)} (com desconto)`
              : parcial ? `Pagar ${fmtMoeda(vPago)} e gerar restante` : 'Confirmar Pagamento'}
          </button>
          <button className={s.btnErp} onClick={onClose} disabled={salvando}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}
